import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight, XCircle } from "lucide-react";
import { walletApi, gbp } from "@/lib/wallet";
import SEO from "@/components/site/SEO";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 12; // 24s total

export default function CustomerTopUpSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(sessionId ? "checking" : "missing"); // checking | paid | pending | failed | expired | missing
  const [txn, setTxn] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    async function poll() {
      try {
        const { data } = await walletApi.get(`/customer/payments/status/${sessionId}`);
        if (cancelled) return;
        setTxn(data);
        if (data.status === "paid" && data.credited) {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (data.status === "failed") {
          setState("failed");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_POLLS) {
          setState("pending");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        attempts.current += 1;
        if (attempts.current >= MAX_POLLS) {
          setState("pending");
        } else {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    }
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-brand-bgSoft text-brand-ink flex items-center justify-center px-4" data-testid="topup-success">
      <SEO title="Top up — confirmation" path="/app/wallet/topup/success" />
      <div className="w-full max-w-xl bg-white border border-brand-line rounded-2xl p-8 shadow-sm">
        {state === "checking" && (
          <Status icon={<Loader2 className="h-10 w-10 animate-spin text-brand-green" />}
            title="Confirming your payment…"
            body="One moment while Stripe finalises your top-up. This usually takes a few seconds." />
        )}
        {state === "paid" && (
          <div data-testid="topup-paid">
            <Status icon={<CheckCircle2 className="h-10 w-10 text-emerald-500" />}
              title="Top-up successful"
              body={`${gbp(txn?.amount)} has been added to your wallet.`} />
            <Link to="/app/wallet" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover px-5 py-2.5 text-sm font-semibold text-white" data-testid="topup-paid-cta">
              Go to wallet <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
        {state === "pending" && (
          <div data-testid="topup-pending">
            <Status icon={<Loader2 className="h-10 w-10 text-amber-500 animate-spin" />}
              title="Still processing"
              body="Stripe is taking a little longer than usual. Your wallet will update automatically — feel free to refresh in a minute." />
            <Link to="/app/wallet" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white border border-brand-line hover:border-brand-green px-5 py-2.5 text-sm font-semibold text-brand-ink">
              Open wallet
            </Link>
          </div>
        )}
        {state === "failed" && (
          <div data-testid="topup-failed">
            <Status icon={<XCircle className="h-10 w-10 text-rose-500" />}
              title="Payment failed"
              body="The card was declined or the payment didn't complete. Your wallet was not charged." />
            <Link to="/app/wallet/topup" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover px-5 py-2.5 text-sm font-semibold text-white">
              Try again
            </Link>
          </div>
        )}
        {state === "expired" && (
          <div data-testid="topup-expired">
            <Status icon={<XCircle className="h-10 w-10 text-rose-500" />}
              title="Session expired"
              body="The Stripe Checkout session timed out before payment completed." />
            <Link to="/app/wallet/topup" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover px-5 py-2.5 text-sm font-semibold text-white">
              Start over
            </Link>
          </div>
        )}
        {state === "missing" && (
          <Status icon={<AlertCircle className="h-10 w-10 text-amber-500" />}
            title="Missing checkout session"
            body="We couldn't find a Stripe session in the URL. Return to your wallet to start a fresh top-up." />
        )}
      </div>
    </div>
  );
}

function Status({ icon, title, body }) {
  return (
    <div className="flex flex-col items-start gap-3">
      {icon}
      <h1 className="font-display font-black text-2xl">{title}</h1>
      <p className="text-sm text-brand-muted">{body}</p>
    </div>
  );
}
